import { useMutation } from '@apollo/client/react';
import type { BillingSubscriptionPriceDetailsFragment } from '@graphql/fragments/billing-subscription/price.graphql';
import {
  CreateCheckoutSessionDocument,
  CreateResubscribeCheckoutSessionDocument,
} from '@graphql/mutations/billing_subscription/core.graphql';
import { CreateWorkspaceDocument } from '@graphql/mutations/workspace/create.graphql';
import { usePrices } from '@hooks/billing-subscription';
import { cookies } from '@modules/auth/cookies';
import { useCallback } from 'react';

type StartCheckoutCommonArgs = {
  /**
   * The checkout's workspace ID. If `createWorkspace` is true this can be `undefined`.
   */
  workspaceId?: string;
  /**
   * Should a workspace be created if one does not exist?
   */
  createWorkspace?: boolean;
  /**
   * The quantity to purchase.
   */
  seats?: number;
  /**
   * Whether the quantity can be adjusted on the Stripe checkout page.
   */
  adjustableSeats?: boolean;
  promotionalCode?: string;
  /** Cello referral code (ucc) that attributed this signup, forwarded for conversion reporting. */
  celloReferralCode?: string;
  withTrial?: boolean;
  successUrl?: string;
  cancelUrl?: string;
};

type StartCheckoutByIntervalAndTypeArgs = StartCheckoutCommonArgs & {
  priceId?: never;
  interval: 'month' | 'year';
  type: 'INDIVIDUAL' | 'TEAM';
  /**
   * Which plan to buy. Defaults to the workspace type's long-standing plan, so a
   * caller that predates Pro keeps selling exactly what it always sold.
   */
  plan?: 'premium' | 'pro' | 'business';
};

type StartCheckoutByPriceIdArgs = StartCheckoutCommonArgs & {
  /**
   * The Blinq price ID (not Stripe price ID).
   */
  priceId: string;
  interval?: never;
  type?: never;
};

type StartCheckoutArgs = StartCheckoutByIntervalAndTypeArgs | StartCheckoutByPriceIdArgs;

/**
 * The plan each workspace type sold before Pro existed. Selection by `interval` and
 * `type` alone stopped being unique the moment a second current INDIVIDUAL product
 * appeared — it then resolved by catalog order and could sell Pro to a caller that
 * meant Premium — so a plan is always pinned, defaulted rather than inferred.
 */
export const DEFAULT_PLAN_BY_TYPE = { INDIVIDUAL: 'premium', TEAM: 'business' } as const;

/**
 * Every plan checkout may resolve. Wider than `usePrices`'s default on purpose:
 * a caller naming an explicit price id has already chosen, and this is the hook
 * that has to honour it. Withholding Pro here would filter the id out before
 * `findPrice` searched, so choosing Pro on the plans screen would throw
 * "Failed to find price" rather than reaching Stripe.
 *
 * Callers selecting by interval and workspace type are unaffected: `findPrice`
 * pins them to `DEFAULT_PLAN_BY_TYPE`, so a wider list cannot reach them.
 */
const CHECKOUT_PLANS = ['premium', 'pro', 'business'] as const;

export function findPrice(
  prices: BillingSubscriptionPriceDetailsFragment[] | undefined,
  args: StartCheckoutArgs,
): BillingSubscriptionPriceDetailsFragment | undefined {
  if (args.priceId) {
    return prices?.find((p) => p.id === args.priceId);
  }

  if (!args.interval || !args.type) {
    return undefined;
  }

  const { interval, type } = args;
  const plan = args.plan ?? DEFAULT_PLAN_BY_TYPE[type];

  return prices?.find(
    (p) => p.interval === interval && p.product?.type === type && p.product?.plan === plan,
  );
}

interface UseCheckout {
  /**
   * Re-directs to a Stripe checkout session returning to the success or cancel URL. If `createWorkspace`
   * is true and `workspaceId` is not provided, a workspace will be created according to the `priceId`.
   * @param args The checkout arguments.
   * @returns Re-directs to a Stripe checkout session.
   */
  startCheckout: (args: StartCheckoutArgs) => Promise<void>;
  /**
   * Re-directs to a Stripe resubscribe checkout session returning to the success or cancel URL. This is for
   * customers resubscribing after cancelling. It will never create a workspace or new Stripe customer.
   * @param args The checkout arguments.
   * @returns Re-directs to a Stripe checkout session.
   */
  startResubscribeCheckout: (args: StartCheckoutArgs) => Promise<void>;
  loading: boolean;
  pricesLoading: boolean;
}

function useCheckout(): UseCheckout {
  const { prices, loading: pricesLoading } = usePrices({ plans: CHECKOUT_PLANS });
  const [createCheckoutSession, { loading: checkoutLoading }] = useMutation(
    CreateCheckoutSessionDocument,
  );
  const [createResubscribeCheckoutSession, { loading: resubscribeCheckoutLoading }] = useMutation(
    CreateResubscribeCheckoutSessionDocument,
  );
  const [createWorkspaceMutation, { loading: creatingWorkspace }] =
    useMutation(CreateWorkspaceDocument);

  const startCheckout = useCallback(
    async ({
      workspaceId,
      seats,
      adjustableSeats,
      promotionalCode,
      celloReferralCode,
      successUrl,
      cancelUrl,
      createWorkspace = false,
      withTrial = true,
      ...pricingArgs
    }: StartCheckoutArgs) => {
      const price = findPrice(prices, pricingArgs as StartCheckoutArgs);

      if (!price) {
        throw new Error(`Failed to find price.`);
      }

      if (!price.product?.type) {
        throw new Error(`Failed to find product type for price "${price.id}".`);
      }

      if (!workspaceId && createWorkspace) {
        const { data } = await createWorkspaceMutation({
          variables: {
            createInput: {
              type: price.product.type,
            },
          },
        });

        workspaceId = data?.createWorkspace?.workspace.id;
      }

      if (!workspaceId) {
        throw new Error(`Cannot start checkout. No workspace ID provided.`);
      }

      const defaultReturnUrl = window.location.href;

      const { data } = await createCheckoutSession({
        variables: {
          input: {
            subscription_price_id: price.id,
            workspace_id: workspaceId,
            success_url: successUrl ?? defaultReturnUrl,
            cancel_url: cancelUrl ?? defaultReturnUrl,
            promotion_code: promotionalCode,
            cello_referral_code: celloReferralCode,
            with_trial: withTrial,
            seats,
            adjustable_seats: adjustableSeats,
            ps_xid: cookies.get('ps_xid'),
            ps_partner_key: cookies.get('ps_partner_key'),
            ps_group_source: cookies.get('ps_group_src'),
          },
        },
      });

      const checkoutUrl = data?.createCheckoutSession?.checkout_url;

      if (!checkoutUrl) {
        throw new Error(`Failed to create checkout session.`);
      }

      window.location.assign(checkoutUrl);
    },
    [createCheckoutSession, createWorkspaceMutation, prices],
  );

  const startResubscribeCheckout = useCallback(
    async ({
      workspaceId,
      seats,
      promotionalCode,
      celloReferralCode,
      successUrl,
      cancelUrl,
      ...pricingArgs
    }: StartCheckoutArgs) => {
      const price = findPrice(prices, pricingArgs as StartCheckoutArgs);

      if (!price) {
        throw new Error(`Failed to find price.`);
      }

      if (!workspaceId) {
        throw new Error(`Cannot start resubscribe checkout. No workspace ID provided.`);
      }

      const defaultReturnUrl = window.location.href;

      const { data } = await createResubscribeCheckoutSession({
        variables: {
          input: {
            subscription_price_id: price.id,
            workspace_id: workspaceId,
            success_url: successUrl ?? defaultReturnUrl,
            cancel_url: cancelUrl ?? defaultReturnUrl,
            promotion_code: promotionalCode,
            cello_referral_code: celloReferralCode,
            seats,
          },
        },
      });

      const checkoutUrl = data?.createResubscribeCheckoutSession?.checkout_url;

      if (!checkoutUrl) {
        throw new Error(`Failed to create checkout session.`);
      }

      window.location.assign(checkoutUrl);
    },
    [createResubscribeCheckoutSession, prices],
  );

  return {
    startCheckout,
    startResubscribeCheckout,
    loading: checkoutLoading || resubscribeCheckoutLoading || creatingWorkspace,
    pricesLoading,
  };
}

export { type StartCheckoutArgs, useCheckout };
